import { useEffect, useState, useRef, useMemo, startTransition } from "react";
import { FaEnvelope, FaPhoneAlt, FaCopy, FaCheck, FaLinkedin, FaSun, FaMoon, FaWhatsapp } from "react-icons/fa";
import { useDevMode } from "./DeveloperMode";
import { useTheme } from "../contexts/ThemeContext";
import { useDebug } from "../contexts/DebugContext";
import { getNavSections } from "../config/sections";
import { useRouter, Link } from "../lib/router";
import { LINKS } from "../config/links";
import { openAppWithFallback } from "../utils/appLinks";
import "./Navbar.css";

const Navbar = () => {
  const [mounted, setMounted] = useState(false);
  // Mobile menu open/closed state
  const [open, setOpen] = useState(false);
  // Whether user has scrolled past 8px threshold (adds shadow class).
  const [scrolled, setScrolled] = useState(false);
  // Contact dropdown visibility
  const [showDropdown, setShowDropdown] = useState(false);
  // Clipboard feedback for email copy
  const [copiedEmail, setCopiedEmail] = useState(false);
  // Clipboard feedback for phone copy
  const [copiedPhone, setCopiedPhone] = useState(false);
  // Currently active section hash for scroll spy highlighting.
  const [activeHash, setActiveHash] = useState("");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    setScrolled(window.scrollY > 8);
    setActiveHash(window.location.hash);
  }, []);
  // Easter egg click counter on logo
  const [logoClickCount, setLogoClickCount] = useState(0);
  // Current tooltip message shown near logo
  const [logoTooltip, setLogoTooltip] = useState("Don't click me");
  // Whether the logo tooltip is visible
  const [showLogoTooltip, setShowLogoTooltip] = useState(false);
  // Separate state for the coder peek (hides immediately on mouse leave, no 300ms delay)
  const [showMaCoder, setShowMaCoder] = useState(false);
  // Tracks actual mouse hover over the logo (not auto-triggered tooltip)
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const logoRef = useRef<HTMLAnchorElement>(null);
  // Explicit `number` type: setTimeout returns `number` in browsers but `NodeJS.Timeout` in Node/SSR.
  // This type annotation is a cross-environment workaround to avoid TypeScript compatibility issues.
  const logoTimeoutRef = useRef<number>(0);
  // Tracks which hover message to show next (cycles through array)
  const hoverIndexRef = useRef(0);
  const themeBtnRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [badgePopupOpen, setBadgePopupOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const badgeWrapperRef = useRef<HTMLSpanElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const { unlocked, registerLogoClick, showLogoTooltipMessage, logoTooltipLong, timeRemainingSecs, lock, logoWiggle } = useDevMode();
  const { isDark, toggleLightDark } = useTheme();
  const { config: debug } = useDebug();
  const { path } = useRouter();
  // Derive sub-page state from router path so it stays in sync with pushState navigation.
  const subPage = path !== "/";

  // Memoized navigation links derived from debug config (supports dynamic section ordering).
  // On sub-pages, prepend `/` so Link triggers SPA navigation back to the portfolio.
  // Also adds a "Home" item at the front.
  const links = useMemo(() => {
    const activeSections = mounted ? debug.SECTIONS : undefined;
    const order = mounted ? debug.ORDER?.SECTIONS : undefined;
    const navLinks = getNavSections(activeSections, order).map((s) => ({
      href: subPage ? `/#${s.id}` : `#${s.id}`,
      label: s.label,
    }));
    if (subPage) {
      navLinks.unshift({ href: "/#top", label: "Home" });
    }
    return navLinks;
  }, [debug.SECTIONS, debug.ORDER?.SECTIONS, subPage, mounted]);

  // Easter egg hover messages shown on first hovers (cycles per session, resets on leave)
  const logoFirstHoverMessages = [
    "Don't click me",
    "seriously, don't",
    "not a button, I swear",
    "this is a trap 🚩",
    "you'll regret this",
    "nothing to see here",
    "last chance to look away",
    "don't. just don't.",
    "hovering won't help",
    "resistance is futile? no wait, it's not. resist!",
    "are you lost?",
    "this isn't what you're looking for",
    "abort mission",
    "turn back now",
  ];

  // Easter egg click messages shown on each logo click (cycles endlessly)
  const logoMessages = [
    "why ain't you listening to me",
    "okay go ahead, now I can't help you anymore",
    "almost there: you can feel it, can't you?",
    "seriously? you're still clicking?",
    "you know what? I'm done. 😤",
    "still here?",
    "you're persistent, I'll give you that.",
    "okay new rule: every 10th click gets a free nothing.",
    "you've unlocked the secret 'waste time' achievement 🏆",
    "plot twist: there is no plot twist.",
    "this is your life now. clicking MA.",
    "404: common sense not found",
    "I'm not saying you're addicted, but...",
    "error 418: I'm a teapot, stop clicking",
    "your dedication is noted. and questioned.",
    "imagine if you put this energy into a side project",
    "you broke the click counter. congrats. it's broken now.",
    "🥇 most persistent clicker 2026",
  ];

  // Increments the dev mode unlock counter on each logo click; shows cycling easter egg messages unless already unlocked
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    registerLogoClick();
    if (unlocked) return;
    const nextCount = logoClickCount + 1;
    // Detect touch (mobile/tablet) vs mouse (desktop) via the native PointerEvent.pointerType.
    // On desktop, hover already showed "Don't click me", so the first click should transition
    // directly into click messages. On mobile, no hover occurs, so the first click shows the
    // intro hover message instead.
    const isTouch =
      window.PointerEvent && e.nativeEvent instanceof PointerEvent ? (e.nativeEvent as PointerEvent).pointerType === "touch" : false;
    if (nextCount === 1 && isTouch) {
      setLogoTooltip(logoFirstHoverMessages[0]);
    } else {
      const clickIdx = isTouch ? nextCount - 2 : nextCount - 1;
      setLogoTooltip(logoMessages[clickIdx % logoMessages.length]);
    }
    setLogoClickCount(nextCount);
    setShowLogoTooltip(true);
    setShowMaCoder(true);
    clearTimeout(logoTimeoutRef.current);
    logoTimeoutRef.current = window.setTimeout(() => {
      setShowLogoTooltip(false);
      setShowMaCoder(false);
    }, 2500);
  };

  // Shows cycling hover warning messages on each mouse enter (resets on leave)
  const handleLogoMouseEnter = () => {
    setIsLogoHovered(true);
    if (unlocked) return;
    const msg = logoFirstHoverMessages[hoverIndexRef.current % logoFirstHoverMessages.length];
    hoverIndexRef.current += 1;
    setLogoTooltip(msg);
    setShowLogoTooltip(true);
    setShowMaCoder(true);
  };

  // Hides tooltip with short delay and resets hover/click counters
  const handleLogoMouseLeave = () => {
    setIsLogoHovered(false);
    setShowMaCoder(false); // hide coder immediately (reverse motion in one go)
    clearTimeout(logoTimeoutRef.current);
    logoTimeoutRef.current = window.setTimeout(() => setShowLogoTooltip(false), 300);
    hoverIndexRef.current = 0;
    setLogoClickCount(0);
  };

  // Adds/removes shadow state on navbar based on scroll threshold.
  // passive: true for performance (scroll listeners cannot call preventDefault(),
  // so passive hint lets browser skip waiting for the handler).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Shows a timed tooltip + coder on the logo when DevModeProvider triggers an idle message
  useEffect(() => {
    startTransition(() => {
      if (!showLogoTooltipMessage) {
        if (!isLogoHovered && logoClickCount === 0) {
          setShowLogoTooltip(false);
          setShowMaCoder(false);
        }
        return;
      }
      setLogoTooltip(showLogoTooltipMessage);
      setShowLogoTooltip(true);
      setShowMaCoder(true);
    });
  }, [showLogoTooltipMessage, isLogoHovered, logoClickCount]);

  // Scroll spy: syncs URL hash and active nav link with the current section in viewport.
  // Debounces scroll events, disables until initial hash-anchored scroll completes,
  // and re-enables on any user interaction (wheel, touch, click, keydown).
  //
  // CRITICAL: The `spyEnabled` flag coordinates with App.tsx's hash-scroll polling system.
  // When App.tsx is auto-scrolling to a hash (lines ~184-255 of App.tsx), Navbar should NOT
  // overwrite the URL hash. Initially disabled if there is a URL hash on mount, enabled when
  // user interacts (wheel/touch/keydown/mousedown listeners below) or when the targeted section
  // scrolls near viewport top (within 100px). The `enableSpy` functions are intentionally stored
  // as separate references (not inline) so they can be properly removed in cleanup.
  useEffect(() => {
    // Skip scroll-spy entirely on sub-pages (privacy, terms, 404) where no portfolio sections exist.
    if (window.location.pathname !== "/") return;

    let timeoutId: number;
    // If a hash is present on mount, do not clear/overwrite it until the page has finished
    // scrolling to that target, or until the user interacts manually with the page.
    let spyEnabled = !window.location.hash;

    const handleScrollSpy = () => {
      if (!spyEnabled) {
        const targetId = window.location.hash.slice(1);
        const targetEl = document.getElementById(targetId);
        if (targetEl) {
          const rect = targetEl.getBoundingClientRect();
          // Once the target element is near the top of the viewport (within 100px), enable scroll spy
          if (Math.abs(rect.top) < 100) {
            spyEnabled = true;
          }
        }
        return;
      }

      // 1. If at the top of the page, clear active hash and URL hash.
      // history.replaceState does NOT fire hashchange event, avoiding feedback loop with App.tsx.
      // UX rationale: at the very top, no section is "active" and the URL should be clean.
      if (window.scrollY < 80) {
        setActiveHash("");
        if (window.location.hash) {
          history.replaceState(null, "", window.location.pathname + window.location.search);
        }
        return;
      }

      // 2. Find which section is currently in viewport (top 1/3 of view)
      let currentSection = "";
      // Upper third of viewport: section becomes "active" when its top enters this zone.
      // Biases toward the section the user is scrolling toward, not the one they're leaving.
      const triggerPoint = window.innerHeight / 3;

      const sectionIds = getNavSections(debug.SECTIONS, debug.ORDER?.SECTIONS).map((s) => s.id);
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= triggerPoint && rect.bottom >= triggerPoint) {
            currentSection = "#" + id;
            break;
          }
        }
      }

      if (currentSection) {
        setActiveHash(currentSection);
        if (window.location.hash !== currentSection) {
          history.replaceState(null, "", currentSection);
        }
      }
    };

    // Debounced scroll listener (zero computations during active scrolling)
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      // 100ms debounce: imperceptible to users but prevents hundreds of executions during fast scroll.
      timeoutId = window.setTimeout(() => {
        handleScrollSpy();
      }, 100);
    };

    const enableSpy = () => {
      spyEnabled = true;
    };

    // Run spy logic initially if there is no hash
    if (spyEnabled) {
      handleScrollSpy();
    }

    window.addEventListener("scroll", debouncedScroll, { passive: true });
    window.addEventListener("wheel", enableSpy, { passive: true });
    window.addEventListener("touchstart", enableSpy, { passive: true });
    window.addEventListener("mousedown", enableSpy, { passive: true });
    window.addEventListener("keydown", enableSpy, { passive: true });

    // Syncs activeHash when URL hash changes externally (e.g., from App.tsx's hash-scroll effect).
    const handleHashChange = () => {
      setActiveHash(window.location.hash);
    };
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("scroll", debouncedScroll);
      window.removeEventListener("wheel", enableSpy);
      window.removeEventListener("touchstart", enableSpy);
      window.removeEventListener("mousedown", enableSpy);
      window.removeEventListener("keydown", enableSpy);
      window.removeEventListener("hashchange", handleHashChange);
      clearTimeout(timeoutId);
    };
  }, [debug.SECTIONS, debug.ORDER?.SECTIONS, path]);

  // Prevents background scrolling when mobile nav menu is open.
  // Belt-and-suspenders: some browsers respect `overflow: hidden` on `html`, others on `body`.
  // Setting both ensures scroll lock works everywhere. Cleanup removes both unconditionally.
  useEffect(() => {
    if (open) {
      document.documentElement.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      document.documentElement.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
    return () => {
      document.documentElement.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    };
  }, [open]);

  // Computes click origin from theme button bounds for a circular theme transition effect.
  // Origin is computed from getBoundingClientRect() and passed to ThemeTransition.tsx.
  // If ThemeTransition changes its origin handling, both this AND DeveloperMode's terminal
  // theme toggle (line ~665 of DeveloperMode.tsx) would break. This coupling chain is undocumented.
  const handleThemeToggle = () => {
    const rect = themeBtnRef.current?.getBoundingClientRect();
    const origin = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : undefined;
    toggleLightDark(origin);
  };

  // Closes mobile menu and dropdown when user clicks outside the navbar element.
  // Uses `click` (not `mousedown`) to avoid interrupting click events on dropdown links.
  // Child elements (copy buttons, contact wrapper) use e.stopPropagation() to prevent
  // this handler from firing when interacting inside the dropdown.
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowDropdown(false);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // Closes both mobile menu and contact dropdown after a nav link is clicked
  const handleNav = () => {
    setOpen(false);
    setShowDropdown(false);
  };

  // Copies email to clipboard with visual feedback icon toggle (check mark then revert).
  // navigator.clipboard.writeText() requires a secure context (HTTPS or localhost).
  // On HTTP (e.g., some dev setups), this silently fails with no try/catch -- known gap.
  // e.stopPropagation() prevents the outside click handler from closing the dropdown.
  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent closing dropdown
    navigator.clipboard.writeText("cs.mohitaroraofficial@gmail.com");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  // Copies phone number to clipboard with visual feedback icon toggle.
  // navigator.clipboard.writeText() silently fails on non-HTTPS -- no try/catch, known gap.
  // e.stopPropagation() prevents the outside click handler from closing the dropdown.
  const handleCopyPhone = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent closing dropdown
    navigator.clipboard.writeText("+919041158436");
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  // -- Dev badge popup handlers --

  const toggleBadgePopup = () => {
    if (!badgePopupOpen && badgeRef.current && badgeWrapperRef.current) {
      const badgeRect = badgeRef.current.getBoundingClientRect();
      const wrapperRect = badgeWrapperRef.current.getBoundingClientRect();
      const pw = 240;
      const gap = 8;

      const viewportTop = badgeRect.bottom + gap;
      let viewportLeft = badgeRect.right - pw;

      if (viewportLeft < 12) viewportLeft = 12;
      if (viewportLeft + pw > window.innerWidth - 12) {
        viewportLeft = window.innerWidth - pw - 12;
      }

      setPopupStyle({
        position: "absolute",
        top: viewportTop - wrapperRect.top,
        left: viewportLeft - wrapperRect.left,
        width: pw,
      });
    }
    setBadgePopupOpen((prev) => !prev);
  };

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBadgePopup();
  };

  const handleBadgeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      toggleBadgePopup();
    }
  };

  const handleLockDevMode = () => {
    lock();
    setBadgePopupOpen(false);
  };

  useEffect(() => {
    if (!badgePopupOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (badgeWrapperRef.current && !badgeWrapperRef.current.contains(e.target as Node)) {
        setBadgePopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [badgePopupOpen]);

  useEffect(() => {
    if (!badgePopupOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setBadgePopupOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [badgePopupOpen]);

  useEffect(() => {
    if (!unlocked) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBadgePopupOpen(false);
      setPopupStyle({});
    }
  }, [unlocked]);

  return (
    <nav ref={navRef} className={`navbar ${mounted && scrolled ? "is-scrolled" : ""}`}>
      <div className="container nav-container">
        {/* Logo - doubles as dev mode unlock trigger; shows easter egg tooltip on hover/click */}
        {/* Stays as <a> (not <Link>) because handleLogoClick always prevents default. */}
         <a
          href={subPage ? "/" : "#top"}
          ref={logoRef}
          className={`logo${unlocked ? " logo-dev-mode" : ""}${logoWiggle ? " logo-wiggle" : ""}${logoTooltipLong ? " logo-wiggle-long" : ""}`}
          onClick={handleLogoClick}
          onMouseEnter={handleLogoMouseEnter}
          onMouseLeave={handleLogoMouseLeave}
        >
          {(!mounted || debug.MA_HOVER_EFFECT === 1) && <span className={`ma-coder${showMaCoder ? " is-visible" : ""}`}>🧑🏻‍💻</span>}
          {mounted && debug.MA_HOVER_EFFECT === 2 && (
            <div className={`ripple-container${showLogoTooltip && isLogoHovered ? " is-active" : ""}`}>
              <span className="ripple-ring" />
              <span className="ripple-ring" />
              <span className="ripple-ring" />
              <span className="ripple-ring" />
            </div>
          )}
          <span className="ma-text">MA</span>
          <span className="ma-dot">.</span>
          {unlocked && timeRemainingSecs !== null && (
            <span className={`dev-badge-wrapper${badgePopupOpen ? " popup-open" : ""}`} ref={badgeWrapperRef}>
              <span
                ref={badgeRef}
                className="dev-badge"
                onClick={handleBadgeClick}
                onKeyDown={handleBadgeKeyDown}
                role="button"
                tabIndex={0}
              >
                <span className="dev-badge-dot" />
                <span className="dev-badge-label">DEV</span>
                <span className="dev-badge-timer">
                  {Math.floor(timeRemainingSecs / 60)}:{(timeRemainingSecs % 60).toString().padStart(2, "0")}
                </span>
              </span>
              <span className="dev-badge-hover-tip">Click for options</span>
              <div className={`dev-badge-popup ${badgePopupOpen ? "is-open" : ""}`} style={popupStyle}>
                <button type="button" className="dev-badge-popup-close" onClick={() => setBadgePopupOpen(false)} aria-label="Close">
                  &times;
                </button>
                <div className="dev-badge-popup-header">
                  <span className="dev-badge-popup-icon">&#x1f527;</span>
                  <span className="dev-badge-popup-title">Dev Mode Active</span>
                </div>
                <button type="button" className="dev-badge-popup-lock-btn" onClick={handleLockDevMode}>
                  Turn off Developer Mode
                </button>
              </div>
            </span>
          )}
          {showLogoTooltip && (
            <span className={`logo-tooltip${logoTooltipLong ? " logo-tooltip-long" : ""}`}>
              {logoTooltip}
            </span>
          )}
        </a>

        {/* Theme toggle button with circular transition origin computation */}
        <button
          ref={themeBtnRef}
          type="button"
          className="theme-toggle"
          onClick={handleThemeToggle}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {!mounted || isDark ? <FaSun /> : <FaMoon />}
        </button>

        {/* Hamburger button for mobile menu toggle */}
        <button
          type="button"
          className={`hamburger ${open ? "is-open" : ""}`}
          aria-label="Toggle menu"
          {...{ "aria-expanded": open }}
          onClick={() => setOpen((v) => !v)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        {/* Desktop and mobile nav links with scroll-spy active highlighting */}
        <ul className={`nav-links ${open ? "is-open" : ""}`}>
          {links.map((l) => (
            <li key={l.href}>
              <Link href={l.href} onClick={handleNav} className={mounted && activeHash === l.href ? "active" : ""}>
                {l.label}
              </Link>
            </li>
          ))}
          {/* Contact dropdown with email copy, phone copy, and LinkedIn link */}
          <li className="nav-contact-wrapper" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`btn-contact ${showDropdown ? "is-active" : ""}`}
              {...{ "aria-expanded": showDropdown }}
              aria-haspopup="true"
              onClick={() => setShowDropdown((v) => !v)}
            >
              Contact
            </button>

            <div className={`contact-dropdown glass-panel ${showDropdown ? "is-open" : ""}`}>
              <div className="dropdown-item-wrapper">
                <a href="mailto:cs.mohitaroraofficial@gmail.com" className="dropdown-item" onClick={handleNav}>
                  <FaEnvelope className="dropdown-icon" />
                  <div className="item-text">
                    <span className="item-label">Send Email</span>
                    <span className="item-val">cs.mohitaroraofficial@gmail.com</span>
                  </div>
                </a>
                <button
                  type="button"
                  className={`copy-btn ${copiedEmail ? "success" : ""}`}
                  onClick={handleCopyEmail}
                  data-cursor-tooltip="Copy email address"
                  aria-label="Copy email address"
                >
                  {copiedEmail ? <FaCheck /> : <FaCopy />}
                </button>
              </div>

              <div className="dropdown-item-wrapper">
                <a href="tel:+919041158436" className="dropdown-item-info-link" onClick={handleNav}>
                  <FaPhoneAlt className="dropdown-icon" />
                  <div className="item-text">
                    <span className="item-label">Call / WhatsApp</span>
                    <span className="item-val">(+91) 9041158436</span>
                  </div>
                </a>
                <div className="dropdown-actions">
                  <a
                    href="https://wa.me/919041158436"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-btn whatsapp-action"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openAppWithFallback("whatsapp://send?phone=919041158436", "https://wa.me/919041158436");
                    }}
                    data-cursor-tooltip="WhatsApp Message"
                    aria-label="WhatsApp Message"
                  >
                    <FaWhatsapp />
                  </a>
                  <button
                    type="button"
                    className={`copy-btn ${copiedPhone ? "success" : ""}`}
                    onClick={handleCopyPhone}
                    data-cursor-tooltip="Copy phone number"
                    aria-label="Copy phone number"
                  >
                    {copiedPhone ? <FaCheck /> : <FaCopy />}
                  </button>
                </div>
              </div>

              <div className="dropdown-item-wrapper">
                <a
                  href={LINKS.LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNav();
                    openAppWithFallback("linkedin://profile/in/arora-sir", LINKS.LINKEDIN);
                  }}
                >
                  <FaLinkedin className="dropdown-icon" />
                  <div className="item-text">
                    <span className="item-label">LinkedIn Message</span>
                    <span className="item-val">linkedin.com/in/arora-sir</span>
                  </div>
                </a>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
